import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogIn } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-black">
      <div className="relative flex items-center justify-center p-8 lg:p-16 carbon-gradient grid-bg">
        <div className="absolute top-6 left-8">
          <BrandLogo size={44} showText textClassName="text-base text-white" />
        </div>

        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-3">
              [ ACCESSO RACE CONTROL ]
            </div>
            <h1 className="font-display font-black uppercase text-5xl lg:text-6xl leading-[0.9]">
              Pit Wall
              <br />
              <span className="text-yellow-500">Login.</span>
            </h1>
            <p className="text-zinc-400 mt-4 max-w-sm">
              Accedi alla console di analisi incidenti F1 25 per emettere verdetti tecnici e imparziali.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="section-h block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="commissario@scuderia.it"
                className="input-field"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="section-h block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-field"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit-button">
              <LogIn className="w-4 h-4" />
              {loading ? "Verifica…" : "Accedi alla Console"}
            </button>

            <div className="text-center pt-2">
              <span className="text-zinc-500 text-sm">Non hai un account? </span>
              <Link to="/register" className="text-yellow-500 hover:text-yellow-400 text-sm font-medium" data-testid="register-link">
                Registrati
              </Link>
            </div>
          </form>
        </div>
      </div>

      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/29309757/pexels-photo-29309757.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-black/30 via-black/10 to-black scanlines" />
        <div className="absolute bottom-10 right-10 left-10 z-10">
          <div className="font-mono-tel text-xs text-yellow-500/80 uppercase mb-2">Race Control</div>
          <p className="font-display font-black uppercase text-3xl leading-tight">
            "Sanzioni eque,
            <br /> dati incontestabili."
          </p>
        </div>
      </div>
    </div>
  );
}
